import { useEffect, useMemo, useRef, useState } from "react";
import PhoneInput from "react-phone-number-input/input-mobile";
import {
  getCountries,
  getCountryCallingCode,
} from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";

const DEFAULT_BROWSER_COUNTRY: CountryCode = "EG";

const TIMEZONE_TO_COUNTRY: Readonly<Record<string, CountryCode>> = {
  "Africa/Cairo": "EG",
  "Africa/Casablanca": "MA",
  "Asia/Riyadh": "SA",
  "Asia/Dubai": "AE",
  "Europe/Istanbul": "TR",
  "Europe/London": "GB",
  "Europe/Paris": "FR",
  "Europe/Berlin": "DE",
  "Europe/Rome": "IT",
  "America/New_York": "US",
  "America/Chicago": "US",
  "America/Denver": "US",
  "America/Los_Angeles": "US",
  "America/Toronto": "CA",
  "America/Vancouver": "CA",
  "Australia/Sydney": "AU",
};

function detectBrowserCountry(): CountryCode {
  if (typeof Intl === "undefined") return DEFAULT_BROWSER_COUNTRY;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fromTz = tz ? (TIMEZONE_TO_COUNTRY[tz] as CountryCode | undefined) : undefined;
    if (fromTz) return fromTz;
  } catch {
    // ignore
  }
  return DEFAULT_BROWSER_COUNTRY;
}

const ARABIC_INDIC_DIGITS = "\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669";
const EASTERN_ARABIC_INDIC_DIGITS = "\u06F0\u06F1\u06F2\u06F3\u06F4\u06F5\u06F6\u06F7\u06F8\u06F9";

function normalizeUnicodeDigits(value: string): string {
  return value
    .split("")
    .map((char) => {
      const ai = ARABIC_INDIC_DIGITS.indexOf(char);
      if (ai >= 0) return String(ai);
      const ei = EASTERN_ARABIC_INDIC_DIGITS.indexOf(char);
      if (ei >= 0) return String(ei);
      return char;
    })
    .join("");
}

export type PhoneInputFieldLabels = {
  /** Accessible label for the country picker button. */
  countryLabel?: string;
  /** Placeholder shown when no country is selected. */
  countryPlaceholder?: string;
  /** Placeholder for the country search input. */
  countrySearchPlaceholder?: string;
  /** Shown when no countries match the search. */
  countryNoResults?: string;
  /**
   * Placeholder for the phone number input.
   * Use `{example}` as a token that will be replaced with a national example number.
   */
  phonePlaceholder?: string;
};

const DEFAULT_LABELS: Required<PhoneInputFieldLabels> = {
  countryLabel: "Country",
  countryPlaceholder: "Country",
  countrySearchPlaceholder: "Search countries...",
  countryNoResults: "No results",
  phonePlaceholder: "e.g. {example}",
};

type PhoneInputFieldProps = {
  value?: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  required?: boolean;
  hasError?: boolean;
  autoComplete?: string;
  defaultCountry?: CountryCode;
  disabled?: boolean;
  name?: string;
  /** Locale tag used for country name display (e.g. "ar", "en"). Defaults to "en". */
  locale?: string;
  /** Localized labels for all UI strings. */
  labels?: PhoneInputFieldLabels;
};

function countryFlagEmoji(countryCode: CountryCode): string {
  return countryCode
    .toUpperCase()
    .split("")
    .map((char: string) => String.fromCodePoint(127397 + char.charCodeAt(0)))
    .join("");
}

function normalizeCountrySearch(value: string): string {
  return normalizeUnicodeDigits(value).trim().toLowerCase();
}

const NATIONAL_PHONE_EXAMPLES: Partial<Record<CountryCode, string>> = {
  EG: "1001112233",
  US: "2015550123",
  GB: "07123456789",
  SA: "0501234567",
  AE: "0501234567",
};

export function PhoneInputField({
  value,
  onChange,
  onBlur,
  required,
  hasError,
  autoComplete = "tel",
  defaultCountry,
  disabled,
  name,
  locale = "en",
  labels: labelsProp,
}: PhoneInputFieldProps) {
  const labels = { ...DEFAULT_LABELS, ...labelsProp };
  const [isCountryPickerOpen, setIsCountryPickerOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [country, setCountry] = useState<CountryCode>(
    () => defaultCountry ?? detectBrowserCountry(),
  );
  const countryPickerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isCountryPickerOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!countryPickerRef.current) {
        return;
      }
      if (!countryPickerRef.current.contains(event.target as Node)) {
        setIsCountryPickerOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isCountryPickerOpen]);

  const countries = useMemo(() => {
    const displayNames = new Intl.DisplayNames([locale, "en"], { type: "region" });
    const source = getCountries().filter(Boolean) as CountryCode[];
    return source
      .map((countryCode) => ({
        countryCode,
        name: displayNames.of(countryCode) ?? countryCode,
        dialCode: `+${getCountryCallingCode(countryCode)}`,
      }))
      .sort((left, right) => left.name.localeCompare(right.name, locale));
  }, [locale]);

  const selectedCountry = defaultCountry ?? country ?? DEFAULT_BROWSER_COUNTRY;
  const selectedCountryOption = useMemo(
    () =>
      countries.find((option) => option.countryCode === selectedCountry) ?? null,
    [countries, selectedCountry],
  );
  const nationalExample =
    NATIONAL_PHONE_EXAMPLES[selectedCountry] ?? "1012345678";
  const resolvedPlaceholder = labels.phonePlaceholder.replace("{example}", nationalExample);
  const normalizedSearch = normalizeCountrySearch(countrySearch);
  const filteredCountries = useMemo(() => {
    if (!normalizedSearch) {
      return countries;
    }
    return countries.filter((option) => {
      const searchTarget = `${option.name} ${option.countryCode} ${option.dialCode}`.toLowerCase();
      return searchTarget.includes(normalizedSearch);
    });
  }, [countries, normalizedSearch]);

  const baseFieldClassName = `h-11 w-full rounded-lg border bg-[color:var(--surface)] px-3 text-sm focus:outline-none ${
    hasError
      ? "border-[color:var(--error-500)] focus:border-[color:var(--error-500)]"
      : "border-[color:var(--border-light)] focus:border-[color:var(--focus-ring)]"
  }`;

  const phoneValue = useMemo(() => {
    const candidate = normalizeUnicodeDigits(value ?? "").replace(
      /[\u200E\u200F\u061C\s]/g,
      "",
    );
    if (!candidate) {
      return undefined;
    }

    return /^\+\d*$/.test(candidate) ? candidate : undefined;
  }, [value]);

  useEffect(() => {
    if (!phoneValue) {
      return;
    }
    const parsed = parsePhoneNumberFromString(phoneValue);
    if (parsed?.country) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reflect externally provided phone value in selected country
      setCountry(parsed.country);
    }
  }, [phoneValue]);

  return (
    <div className="grid grid-cols-[40%_minmax(0,1fr)] gap-2 sm:grid-cols-[30%_minmax(0,1fr)]">
      <div className="relative" ref={countryPickerRef}>
        <button
          type="button"
          aria-label={labels.countryLabel}
          aria-haspopup="listbox"
          aria-expanded={isCountryPickerOpen}
          disabled={disabled}
          onClick={() => {
            setIsCountryPickerOpen((current) => !current);
            setCountrySearch("");
          }}
          className={`${baseFieldClassName} flex items-center justify-between px-2`}
        >
          <span className="truncate text-sm text-[color:var(--text-primary)]">
            {selectedCountryOption
              ? `${countryFlagEmoji(selectedCountryOption.countryCode)} ${selectedCountryOption.countryCode}`
              : labels.countryPlaceholder}
          </span>
          <span className="text-xs text-[color:var(--text-muted)]" aria-hidden="true">
            {isCountryPickerOpen ? "\u25B2" : "\u25BC"}
          </span>
        </button>
        {isCountryPickerOpen ? (
          <div className="absolute z-20 mt-1 w-[min(24rem,80vw)] rounded-lg border border-[color:var(--border-light)] bg-[color:var(--surface)] p-2 shadow-[var(--shadow-sm)]">
            <input
              value={countrySearch}
              onChange={(event) => setCountrySearch(event.target.value)}
              autoFocus
              placeholder={labels.countrySearchPlaceholder}
              className={baseFieldClassName}
            />
            <ul
              role="listbox"
              aria-label={labels.countryLabel}
              className="mt-2 max-h-60 overflow-y-auto"
            >
              {filteredCountries.length > 0 ? (
                filteredCountries.map((option) => (
                  <li key={option.countryCode}>
                    <button
                      type="button"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setCountry(option.countryCode);
                        setCountrySearch("");
                        setIsCountryPickerOpen(false);
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm hover:bg-[color:var(--background-muted)]"
                    >
                      <span className="truncate text-[color:var(--text-primary)]">
                        {countryFlagEmoji(option.countryCode)} {option.name}
                      </span>
                      <span className="ml-3 text-[color:var(--text-muted)]">
                        {option.dialCode}
                      </span>
                    </button>
                  </li>
                ))
              ) : (
                <li className="px-2 py-2 text-sm text-[color:var(--text-muted)]">
                  {labels.countryNoResults}
                </li>
              )}
            </ul>
          </div>
        ) : null}
      </div>
      <PhoneInput
        defaultCountry={selectedCountry}
        value={phoneValue}
        onChange={(nextValue: string | undefined) => {
          const normalized = normalizeUnicodeDigits(nextValue ?? "").replace(
            /[\u200E\u200F\u061C\s]/g,
            "",
          );
          const resolvedValue = /^\+\d*$/.test(normalized) ? normalized : "";
          onChange(resolvedValue);
        }}
        onBlur={onBlur}
        placeholder={resolvedPlaceholder}
        required={required}
        autoComplete={autoComplete}
        disabled={disabled}
        name={name}
        className={baseFieldClassName}
      />
    </div>
  );
}
